import { SHARED_LABELS } from "@markeroo/shared/strings";
import type { GradeLevel, PaperType } from "@markeroo/shared";

/**
 * Grade-aware and dynamic label generation functions.
 */

/**
 * Get grade-appropriate label for paper type.
 *
 * Opinion/Argument papers use different terminology:
 * - Grades 3-5: "Opinion Piece"
 * - Grades 6-12: "Argument"
 *
 * Returns "Not set" for null paper types (e.g., Google Classroom imports).
 */
export function getPaperTypeLabel(
  paperType: PaperType | null,
  gradeLevel?: GradeLevel | null
): string {
  if (paperType === null) {
    return "Not set";
  }

  if (paperType === "opinion_argument") {
    const grade = gradeLevel ? parseInt(gradeLevel, 10) : null;
    return grade && grade <= 5 ? "Opinion Piece" : "Argument";
  }

  const labels: Record<PaperType, string> = {
    opinion_argument: "Argument", // Default for grades 6+
    informative_explanatory: "Informative/Explanatory",
    narrative: "Narrative",
    other: "Other",
  };

  return labels[paperType] || paperType;
}

/**
 * Get issue count label with pluralization.
 */
export function getIssueLabel(count: number | null | undefined): string {
  if (count === null || count === undefined) return "";
  if (count === 0) return SHARED_LABELS.NO_ISSUES;
  return `${count} ${count === 1 ? SHARED_LABELS.ISSUE : SHARED_LABELS.ISSUES}`;
}

/**
 * Format regrade reason into human-readable message.
 */
export function formatRegradeReason(reason: {
  reason: string;
  metadata?: Record<string, unknown>;
}): string {
  const { reason: type, metadata } = reason;

  switch (type) {
    case "criteria_modified":
      return "Criteria were changed";

    case "student_ela_grade_changed": {
      const oldGrade = metadata?.old_grade as string | undefined;
      const newGrade = metadata?.new_grade as string | undefined;
      return `Student’s grade level changed from ${oldGrade || "?"} to ${newGrade || "?"}`;
    }

    case "student_accessibility_changed":
      return "Student’s ELL status changed";

    case "assignment_instructions_changed":
      return "Assignment instructions changed";

    default:
      return "Paper settings changed";
  }
}

/**
 * Simple pluralization helper.
 */
export function plural(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

/**
 * Remaining count label (e.g., "3 remaining").
 */
export function remaining(count: number): string {
  return `${count} remaining`;
}

/**
 * Character count with minimum requirement.
 */
export function charactersWithMin(count: number, min: number): string {
  return `${count} characters (minimum ${min})`;
}

/**
 * Assignment submission status labels.
 */
export const assignmentLabels = {
  noSubmissionsYet: "No submissions yet",
  submissionsHighlighted: (count: number) =>
    `${count} ${count === 1 ? 'submission' : 'submissions'} highlighted`,
  submissionsReadyToHighlight: (count: number) =>
    `${count} ${count === 1 ? 'submission' : 'submissions'} ready to highlight`,
  noSubmissionYet: "No submission yet",
  studentsSubmitted: (current: number, total: number) =>
    `${current}/${total} students submitted`,
  allStudentsSubmitted: "All students submitted",
  allSubmissionsHighlighted: "All submissions highlighted",
  allSubmissionsHighlightedAndShared: "All submissions highlighted and shared",
  submissionsShared: (shared: number, total: number) =>
    `${shared}/${total} shared`,
} as const;

/**
 * Class/assignment helper labels.
 */
export function createAssignmentFor(name: string): string {
  return `Create a new assignment for ${name}.`;
}

export function criteriaWillBeApplied(count: number): string {
  return `${count} ${count === 1 ? 'focus area' : 'focus areas'} will be applied`;
}

export function charactersRemaining(remaining: number): string {
  return `${remaining} characters remaining`;
}

export function highlightingBasedOn(count: number): string {
  return `Highlighting based on ${count} ${count === 1 ? 'focus area' : 'focus areas'}`;
}

export function earlierPapers(count: number): string {
  return `earlier ${count === 1 ? 'paper' : 'papers'}`;
}

export function papersForName(name: string): string {
  return `${name}'s Papers`;
}

export function highlightNPapers(count: number): string {
  return `Highlight ${count} ${count === 1 ? 'paper' : 'papers'}`;
}

export function studentWillSeeFeedbackForAllPapers(name: string): string {
  return `${name} will see feedback for all papers listed above.`;
}

export function studentCanViewFeedbackUsingLink(name: string): string {
  return `${name} can view feedback for all papers listed above using this link.`;
}

export function shareWithStudent(firstName: string): string {
  return `Share with ${firstName}`;
}

/**
 * Usage limit messaging.
 */
export function usageLimitReached(limit: number, unit: string): string {
  return `You’ve reached your monthly limit of ${limit} ${unit}.`;
}
